/**
 * Форматы данных телеметрии для карточек, маршрутов и календаря.
 * Даты показываются в часовом поясе браузера; свежесть связи определяет backend.
 */
/**
 * Форматирует километраж для отображения в ячейке календаря.
 * @param {number} km
 * @returns {string}
 */
export function formatKmShort(km) {
  if (km >= 1000) return `${(km / 1000).toFixed(1)}т`;
  return `${Math.round(km)}`;
}

/**
 * Форматирует километраж точно, с разделителем тысяч.
 * @param {number} km
 * @returns {string}
 */
export function formatKmExact(km) {
  if (km === null || km === undefined) return "—";
  return `${km.toLocaleString("ru-RU", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  })} км`;
}

/**
 * Возвращает отображаемое имя транспортного средства.
 * @param {Object} vehicle
 * @returns {string}
 */
export function getVehicleDisplayName(vehicle) {
  const parts = [vehicle.manufacturer, vehicle.model]
    .filter(Boolean)
    .join(" ")
    .trim();
  return parts || vehicle.full_name || `Авто #${vehicle.id}`;
}

/**
 * Возвращает отображаемое имя трекера.
 * @param {Object} vehicle
 * @returns {string}
 */
export function getVehicleTrackerName(vehicle) {
  return (
    vehicle.telemetry_tracker.name ||
    `Трекер #${vehicle.telemetry_tracker.id}`
  );
}

/**
 * Возвращает скорость транспортного средства в виде строки.
 * @param {Object} vehicle
 * @returns {string}
 */
export function getVehicleSpeed(vehicle) {
  const pos = vehicle.telemetry_current_position;
  if (!pos) return "";
  const speed = parseFloat(pos.speed);
  return isNaN(speed) ? "—" : `${Math.round(speed)} км/ч`;
}

/**
 * Возвращает текстовый статус транспортного средства.
 * @param {Object} vehicle
 * @returns {string}
 */
export function getVehicleStatusLabel(vehicle) {
  const pos = vehicle.telemetry_current_position;
  if (!pos) return "Нет данных";
  return parseFloat(pos.speed) > 0 ? "В движении" : "Стоит";
}

/**
 * Возвращает CSS-класс статуса транспортного средства.
 * @param {Object} vehicle
 * @returns {string}
 */
export function getVehicleStatusClass(vehicle) {
  const pos = vehicle.telemetry_current_position;
  if (!pos) return "status--offline";
  return parseFloat(pos.speed) > 0
    ? "status--moving"
    : "status--idle";
}

/** Возвращает короткую дату записи и полную подпись для подсказки в карточке. */
export function getVehicleUpdatedAt(vehicle) {
  const recordedAt = vehicle.telemetry_current_position?.recorded_at;
  if (!recordedAt) return null;

  return {
    text: formatShortDateTime(recordedAt),
    description: `Обновлено: ${formatDateTime(recordedAt)}`,
  };
}

/**
 * Форматирует ISO-строку в время HH:MM:SS.
 * @param {string} isoString
 * @returns {string}
 */
export function formatTime(isoString) {
  if (!isoString) return "—";
  const date = new Date(isoString);
  return date.toLocaleTimeString("ru-RU", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

/**
 * Форматирует ISO-строку в дату и время DD.MM.YYYY HH:MM:SS.
 * @param {string} isoString
 * @returns {string}
 */
export function formatDateTime(isoString) {
  if (!isoString) return "—";
  const date = new Date(isoString);
  return date.toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

/**
 * Форматирует строку даты YYYY-MM-DD в DD.MM.YYYY.
 * @param {string} dateStr
 * @returns {string}
 */
export function formatDate(dateStr) {
  if (!dateStr) return "";
  const [y, m, d] = dateStr.split("-");
  return `${d}.${m}.${y}`;
}

/**
 * Считает суммарный километраж из карты calendarStats за диапазон дат.
 * @param {Object} calendarStats - { "YYYY-MM-DD": km }
 * @param {string} from - "YYYY-MM-DD"
 * @param {string} to - "YYYY-MM-DD"
 * @returns {number}
 */
export function sumMileageForRange(calendarStats, from, to) {
  if (!from || !to || !calendarStats) return 0;
  let total = 0;
  for (const [date, km] of Object.entries(calendarStats)) {
    if (date >= from && date <= to) {
      total += parseFloat(km) || 0;
    }
  }
  return total;
}

/** Возвращает HH:mm сегодня и DD.MM HH:mm в другие дни в локальном часовом поясе. */
export function formatShortDateTime(isoString) {
  if (!isoString) return "";
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "";

  const isToday = date.toDateString() === new Date().toDateString();
  const time = date.toLocaleTimeString("ru-RU", {
    hour: "2-digit",
    minute: "2-digit",
  });
  if (isToday) return time;

  const day = date.toLocaleDateString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
  });
  return `${day} ${time}`;
}
